// Lazy-mount hint: returns true once the wrapped element has entered the
// viewport, then sticks at true. Widgets gate their data hooks on this
// so off-screen ones don't acquire shared-WS subscriptions or burn rAF
// budget for invisible candles.
//
// `stickyOnceVisible: false` mode would let us tear down hooks for fully
// scrolled-out widgets, but the churn (re-fetching history every time
// the user scrolls back) probably costs more than it saves. Sticky is
// the safer default.

import { useEffect, useRef, useState } from 'react';

// Walk up the DOM to the nearest scrollable ancestor. IntersectionObserver
// needs an explicit `root` when the observed element lives inside a
// non-viewport scroll container — without it, scrolling INSIDE the
// container never triggers intersection, so widgets stay pending forever.
function findScrollParent(node) {
  let el = node?.parentElement || null;
  while (el) {
    const style = getComputedStyle(el);
    const oy = style.overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null; // null = use browser viewport
}

export function useInViewport({ rootMargin = '200px', stickyOnceVisible = true } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const root = findScrollParent(ref.current);
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisible(true);
          if (stickyOnceVisible) obs.disconnect();
        } else if (!stickyOnceVisible) {
          setVisible(false);
        }
      }
    }, { root, rootMargin });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [rootMargin, stickyOnceVisible]);

  return [ref, visible];
}
